/**
 * @description Gateway field registry — factory pattern for gateway type configurations.
 * Each gateway type registers its credential fields, config options, webhook fields,
 * environment options, and validation rules. The admin GatewayConfig page reads from
 * this registry dynamically, making gateways interchangeable (like Resend in outreach).
 *
 * Key features:
 * - Adding a new gateway = adding a new entry to GATEWAY_REGISTRY
 * - Field definitions include label, description, placeholder, validation, sensitivity, and defaults
 * - Environment options per gateway (sandbox/production variants)
 * - Config fields with types (text, number, select, toggle) for dynamic form rendering
 *
 * @dependencies
 * - config: API_URL for deriving webhook endpoint URLs
 *
 * @notes
 * - Field names MUST match the backend credential/config schema exactly
 * - Elavon fields derived from ElavonConfig.CREDENTIAL_SCHEMA and ElavonGateway constructor
 * - Backend validation (ElavonConfig.validateCredentials) is the source of truth;
 *   client-side validation here mirrors it for instant feedback but the server always re-validates
 * - Sensitive fields are masked in UI and use password input type
 */

import config from '../services/api/config';

// =============================================================================
// Elavon EPG Configuration
// Matches: backend/gateways/elavon/ElavonConfig.js
// =============================================================================

const ELAVON_ENVIRONMENTS = [
  { value: 'sandbox', label: 'Sandbox/UAT' },
  { value: 'production_us', label: 'Production (US)' },
  { value: 'production_eu', label: 'Production (EU)' }
];

// Matches ElavonConfig.SHOPPER_INTERACTIONS
const ELAVON_SHOPPER_INTERACTIONS = [
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'mailOrder', label: 'Mail Order' },
  { value: 'telephoneOrder', label: 'Telephone Order' },
  { value: 'merchantInitiated', label: 'Merchant Initiated' },
  { value: 'inPerson', label: 'In Person' }
];

// Matches ElavonConfig.CREDENTIAL_SCHEMA field names and validation rules
const ELAVON_CREDENTIAL_FIELDS = [
  {
    key: 'secretKey',
    label: 'Secret Key',
    description: 'Basic Auth password — must start with sk_',
    placeholder: 'sk_your-secret-key',
    sensitive: true,
    required: true,
    validate: (value) => {
      if (!value || !value.trim()) return 'Secret Key is required';
      if (!value.startsWith('sk_')) return 'Secret Key must start with sk_';
      return null;
    }
  },
  {
    key: 'publicKey',
    label: 'Public Key',
    description: 'Client-side HPF JS SDK initialization — must start with pk_',
    placeholder: 'pk_your-public-key',
    sensitive: false,
    required: true,
    validate: (value) => {
      if (!value || !value.trim()) return 'Public Key is required';
      if (!value.startsWith('pk_')) return 'Public Key must start with pk_';
      return null;
    }
  },
  {
    key: 'integratorId',
    label: 'Integrator ID',
    description: 'Assigned by Elavon during integration onboarding',
    placeholder: 'your-integrator-id',
    sensitive: false,
    required: true,
    validate: (value) => {
      if (!value || !value.trim()) return 'Integrator ID is required';
      return null;
    }
  }
];

// Webhook authentication fields — stored in super_admin_gateways.config JSONB
const ELAVON_WEBHOOK_FIELDS = [
  {
    key: 'webhookSharedSecret',
    label: 'Webhook Shared Secret',
    description: 'HMAC-SHA512 signing secret for webhook signature verification',
    placeholder: 'your-webhook-shared-secret',
    sensitive: true,
    required: false,
    validate: null
  },
  {
    key: 'webhookSignerId',
    label: 'Webhook Signer ID',
    description: 'Signer identifier included in webhook X-Converge-SignerID header',
    placeholder: 'your-signer-id',
    sensitive: false,
    required: false,
    validate: null
  },
  {
    key: 'webhookBasicUser',
    label: 'Webhook Basic Auth Username',
    description: 'Basic Auth username for webhook endpoint authentication',
    placeholder: 'your-webhook-username',
    sensitive: false,
    required: false,
    validate: null
  },
  {
    key: 'webhookBasicPass',
    label: 'Webhook Basic Auth Password',
    description: 'Basic Auth password for webhook endpoint authentication',
    placeholder: 'your-webhook-password',
    sensitive: true,
    required: false,
    validate: null
  }
];

// Gateway behavior config — stored in super_admin_gateways.config JSONB
// Defaults match ElavonConfig constants and ElavonGateway constructor defaults
const ELAVON_CONFIG_FIELDS = [
  {
    key: 'doSendReceipt',
    label: 'Send Receipt via EPG',
    description: 'Let Elavon send transaction receipts to the shopper email (default: off — Lynx handles receipts)',
    type: 'toggle',
    defaultValue: false
  },
  {
    key: 'defaultShopperInteraction',
    label: 'Default Shopper Interaction',
    description: 'How the shopper typically interacts with the payment form',
    type: 'select',
    options: ELAVON_SHOPPER_INTERACTIONS,
    defaultValue: 'ecommerce'
  },
  {
    key: 'defaultCurrencyCode',
    label: 'Default Currency Code',
    description: 'ISO 4217 currency code for transactions (e.g., USD, EUR, GBP)',
    type: 'text',
    placeholder: 'USD',
    defaultValue: 'USD'
  },
  {
    key: 'timeout',
    label: 'API Timeout (ms)',
    description: 'Max wait time for EPG API responses — EPG recommends 30000ms (30s)',
    type: 'number',
    placeholder: '30000',
    defaultValue: 30000
  },
  {
    key: 'retryAttempts',
    label: 'Retry Attempts',
    description: 'Number of retries on transient failures (5xx, network) — EPG recommends 3 or fewer',
    type: 'number',
    placeholder: '3',
    defaultValue: 3
  },
  {
    key: 'retryDelay',
    label: 'Retry Base Delay (ms)',
    description: 'Base delay between retries — exponential backoff with jitter applied automatically',
    type: 'number',
    placeholder: '1000',
    defaultValue: 1000
  }
];

// =============================================================================
// Gateway Registry — Add new gateway types here
// =============================================================================

const GATEWAY_REGISTRY = {
  elavon: {
    type: 'elavon',
    label: 'Elavon EPG',
    description: 'Elavon Payment Gateway — card-not-present payments via Hosted Payment Fields',
    available: true,
    environments: ELAVON_ENVIRONMENTS,
    credentialFields: ELAVON_CREDENTIAL_FIELDS,
    webhookFields: ELAVON_WEBHOOK_FIELDS,
    configFields: ELAVON_CONFIG_FIELDS,
    webhookUrl: `${config.API_URL}/webhooks/elavon`,
    docsUrl: 'https://developer.elavon.com/products/en-uk/elavon-payment-gateway/v1'
  }
  // Future gateways:
  // stripe: { type: 'stripe', label: 'Stripe', credentialFields: [...], ... }
  // square: { type: 'square', label: 'Square', credentialFields: [...], ... }
};

/**
 * Get the list of available gateway types for the dropdown
 * @returns {Array<{value: string, label: string, available: boolean}>}
 */
export function getGatewayTypes() {
  return Object.values(GATEWAY_REGISTRY).map(gw => ({
    value: gw.type,
    label: gw.label,
    available: gw.available
  }));
}

/**
 * Get the full config for a specific gateway type
 * @param {string} gatewayType - e.g., 'elavon'
 * @returns {object|null} Gateway config or null if unknown
 */
export function getGatewayConfig(gatewayType) {
  return GATEWAY_REGISTRY[gatewayType] || null;
}

/**
 * Validate all credential fields for a given gateway type (client-side)
 * Backend ElavonConfig.validateCredentials() is the authoritative validator.
 * @param {string} gatewayType - e.g., 'elavon'
 * @param {object} credentials - Credential values to validate
 * @returns {object} - { isValid: boolean, errors: {[fieldKey]: string} }
 */
export function validateGatewayCredentials(gatewayType, credentials) {
  const gwConfig = GATEWAY_REGISTRY[gatewayType];
  if (!gwConfig) return { isValid: false, errors: { _general: 'Unknown gateway type' } };

  const errors = {};

  gwConfig.credentialFields.forEach(field => {
    if (field.validate) {
      const error = field.validate(credentials[field.key]);
      if (error) errors[field.key] = error;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Get default config values for a gateway type
 * @param {string} gatewayType - e.g., 'elavon'
 * @returns {object} - Default config values keyed by field key
 */
export function getDefaultConfig(gatewayType) {
  const gwConfig = GATEWAY_REGISTRY[gatewayType];
  if (!gwConfig) return {};

  const defaults = {};
  gwConfig.configFields.forEach(field => {
    if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    }
  });
  return defaults;
}

export default GATEWAY_REGISTRY;
